import { useState } from 'react';
import {
  ArrowRight,
  Briefcase,
  Check,
  ChevronDown,
  Copy,
  Globe,
  GripVertical,
  Plus,
  Sparkles,
  Users,
  Zap,
} from 'lucide-react';
import { markOnboardingSeen } from '../../utils/b2bOnboarding';

/**
 * OnboardingTour — first-visit guided tour for /b2b/* surface.
 * Each step shows a mini visual rendered with the SAME components / tokens
 * the recruiter will see on the real screens (jobs list, screening editor,
 * candidate detail), so the tour stays in sync as the UI evolves.
 *
 * Persistence helpers live in `utils/b2bOnboarding.js`.
 */

const STEPS = [
  {
    eyebrow: 'Welcome',
    title: 'AI-powered screening for your candidates',
    description: 'Send a candidate a link. They answer 8–12 questions written specifically for their CV and your role. You get back a verdict, a BARS scorecard, and a recruiter-grade report you can pass to the hiring manager — in minutes.',
    Visual: WelcomeVisual,
  },
  {
    eyebrow: 'Step 1 of 5',
    title: 'Build a job brief',
    description: 'Drag-and-drop sections — About the job, Responsibilities, Tech stack — feed every AI agent. Pick the language for AI reports too: every prompt and every report respects it.',
    Visual: JobsVisual,
  },
  {
    eyebrow: 'Step 2 of 5',
    title: 'Add a candidate — get an instant fit verdict',
    description: 'Paste a resume in structured sections. The AI fit analyzer runs in the background and stamps each candidate with one of 5 verdicts (Ideal → No fit) plus screening topics to probe in the next step.',
    Visual: CandidateVisual,
  },
  {
    eyebrow: 'Step 3 of 5',
    title: 'Generate the screening with AI',
    description: 'Pick categories (hard skills / behavioral / motivation). The agent writes questions anchored in this candidate\'s actual experience and the job. Review, edit, send the unique link.',
    Visual: ScreeningVisual,
  },
  {
    eyebrow: 'Step 4 of 5',
    title: 'Read the report — not just the answers',
    description: 'When the candidate submits, you get a BARS scorecard across 8 behavioral scales with verbatim evidence quotes, plus a Markdown summary (Verdict / Strengths / Risks / Open questions) you can pass to the hiring manager.',
    Visual: ReportVisual,
  },
  {
    eyebrow: 'Step 5 of 5',
    title: 'Track quota and AI spend',
    description: 'Every screening counts toward your plan quota. Open Billing to see usage, AI spend per agent, and upgrade if you need more screenings. Cancel auto-renew anytime.',
    Visual: BillingVisual,
  },
];

export default function OnboardingTour({ onComplete, replay = false }) {
  const [step, setStep] = useState(0);
  const isLast = step === STEPS.length - 1;
  const cur = STEPS[step];
  const VisualComp = cur.Visual;

  const finish = () => {
    markOnboardingSeen();
    onComplete?.({ replay, completed: true });
  };

  const skip = () => {
    markOnboardingSeen();
    onComplete?.({ replay, completed: false });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in-up"
      style={{ background: 'rgba(26, 31, 54, 0.55)', backdropFilter: 'blur(4px)' }}
      role="dialog"
      aria-modal="true"
      aria-label="Onboarding tour"
    >
      <div className="w-full max-w-[560px] bg-white rounded-3xl overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="px-7 pt-6 pb-5">
          <div className="flex items-center justify-between mb-4">
            <span className="text-[11px] uppercase tracking-[0.16em] font-bold text-accent">
              {cur.eyebrow}
            </span>
            <button
              type="button"
              onClick={skip}
              className="text-[12px] text-ink-3 hover:text-ink-1 underline underline-offset-4 transition"
            >
              {replay ? 'Close' : 'Skip tour'}
            </button>
          </div>
          <div className="h-48 rounded-2xl overflow-hidden">
            <VisualComp />
          </div>
        </div>

        {/* Body */}
        <div className="px-7 pb-6">
          <h2 className="text-[24px] font-bold leading-tight text-ink-1 mb-2">
            {cur.title}
          </h2>
          <p className="text-[14px] leading-relaxed text-ink-2">
            {cur.description}
          </p>
        </div>

        {/* Footer */}
        <div className="px-7 pb-6 flex items-center justify-between border-t border-black/5 pt-5">
          <div className="flex gap-1.5 items-center" aria-hidden="true">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className="h-1.5 rounded-full transition-all duration-300"
                style={{
                  width: i === step ? 24 : 8,
                  background:
                    i === step
                      ? 'var(--tw-color-accent, #F47920)'
                      : i < step
                      ? '#FAB87E'
                      : '#E2E5EE',
                }}
              />
            ))}
          </div>
          <div className="flex gap-2">
            {step > 0 && (
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                className="px-4 py-2 rounded-xl text-sm font-semibold text-ink-2 hover:bg-canvas transition"
              >
                Back
              </button>
            )}
            <button
              type="button"
              onClick={() => (isLast ? finish() : setStep(step + 1))}
              className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-4 py-2 rounded-xl transition"
            >
              {isLast ? (
                <>
                  <Check className="w-4 h-4" />
                  Got it
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- per-step visuals ----------------
   All mini-scenes use the SAME design tokens as the real pages so the tour
   remains visually faithful as the UI evolves (accent, ink-1/2/3, canvas,
   surface, rounded-2xl, shadow-card).
*/

function WelcomeVisual() {
  return (
    <div className="w-full h-full relative flex items-center justify-center"
      style={{ background: 'linear-gradient(135deg, #FFE6D2 0%, #FFD1A8 50%, #F47920 200%)' }}>
      <div className="absolute inset-0 opacity-50"
        style={{ background: 'radial-gradient(circle at 25% 25%, white, transparent 55%)' }} />
      <div className="relative flex flex-col items-center gap-2">
        <div className="w-14 h-14 rounded-2xl bg-white shadow-card flex items-center justify-center">
          <Sparkles className="w-7 h-7 text-accent" strokeWidth={1.6} />
        </div>
        <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-ink-1/80">
          JobSkills B2B · AI screening
        </span>
      </div>
    </div>
  );
}

function JobsVisual() {
  return (
    <div className="w-full h-full bg-canvas px-4 py-3.5 flex flex-col gap-2">
      {/* mini "New job" header */}
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider font-bold text-ink-3">Jobs · 2</span>
        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-white bg-accent px-2 py-1 rounded-lg">
          <Plus className="w-3 h-3" />
          New job
        </span>
      </div>
      {/* job card 1 — active */}
      <div className="bg-white rounded-xl shadow-card px-3 py-2.5 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[12px] font-bold text-ink-1 truncate">Senior Backend Engineer</span>
            <span className="text-[8px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">Active</span>
          </div>
          <p className="text-[10px] text-ink-3 flex items-center gap-2">
            <span>Engineering · Remote</span>
            <span className="inline-flex items-center gap-1 text-ink-3">
              <Globe className="w-2.5 h-2.5" />
              EN
            </span>
          </p>
        </div>
        <div className="flex items-center gap-1 text-ink-3 text-xs">
          <Users className="w-3 h-3" />
          <span>3</span>
        </div>
      </div>
      {/* job card 2 — draft */}
      <div className="bg-white rounded-xl shadow-card px-3 py-2.5 flex items-center gap-3 opacity-80">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[12px] font-bold text-ink-1 truncate">Frontend Developer</span>
            <span className="text-[8px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">Draft</span>
          </div>
          <p className="text-[10px] text-ink-3 flex items-center gap-2">
            <span>Engineering · Berlin</span>
            <span className="inline-flex items-center gap-1 text-ink-3">
              <Globe className="w-2.5 h-2.5" />
              DE
            </span>
          </p>
        </div>
        <div className="flex items-center gap-1 text-ink-3 text-xs">
          <Users className="w-3 h-3" />
          <span>0</span>
        </div>
      </div>
      {/* drag affordance hint — block list inside constructor */}
      <div className="bg-white rounded-xl shadow-card px-3 py-1.5 flex items-center gap-2 mt-1">
        <GripVertical className="w-3 h-3 text-ink-3" />
        <span className="text-[10px] font-bold text-ink-1">Tech stack</span>
        <span className="text-[10px] text-ink-3 ml-auto">drag to reorder</span>
      </div>
    </div>
  );
}

function CandidateVisual() {
  return (
    <div className="w-full h-full bg-canvas p-4 flex flex-col gap-2">
      {/* job header */}
      <div className="flex items-center gap-2 mb-1">
        <Briefcase className="w-3.5 h-3.5 text-ink-3" />
        <span className="text-[11px] font-bold text-ink-1">Senior Backend Engineer</span>
        <span className="text-[8px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 ml-auto">Active</span>
      </div>
      {/* candidates list with fit-verdict badges (the new bit) */}
      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        <CandidateRow initials="MC" name="Mira Chen" email="mira@example.com" verdictTone="emerald" verdictLabel="Ideal" />
        <CandidateRow initials="AP" name="Alex Park" email="alex@example.com" verdictTone="lime" verdictLabel="Good" />
        <CandidateRow initials="SK" name="Sam Khan" email="sam@example.com" verdictTone="amber" verdictLabel="Avg" />
        <CandidateRow initials="DV" name="Dasha V." email="dv@example.com" verdictTone="gray" verdictLabel="Analyzing" analyzing />
      </div>
      {/* hint */}
      <div className="flex items-center gap-1.5 text-[10px] text-ink-3 mt-1">
        <Sparkles className="w-3 h-3 text-accent" />
        <span>Verdicts appear within ~10 seconds of adding a CV.</span>
      </div>
    </div>
  );
}

function CandidateRow({ initials, name, email, verdictTone, verdictLabel, analyzing = false }) {
  const tones = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    lime:    'bg-lime-50 text-lime-700 border-lime-200',
    amber:   'bg-amber-50 text-amber-700 border-amber-200',
    orange:  'bg-orange-50 text-orange-700 border-orange-200',
    red:     'bg-red-50 text-red-700 border-red-200',
    gray:    'bg-gray-50 text-gray-500 border-gray-200',
  };
  const dots = {
    emerald: 'bg-emerald-500',
    lime:    'bg-lime-500',
    amber:   'bg-amber-500',
    orange:  'bg-orange-500',
    red:     'bg-red-500',
    gray:    'bg-gray-300',
  };
  return (
    <div className="flex items-center gap-2.5 px-3 py-2 border-b border-black/5 last:border-b-0">
      <div className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center text-accent text-[9px] font-bold">
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-bold text-ink-1 truncate">{name}</p>
        <p className="text-[9px] text-ink-3 truncate">{email}</p>
      </div>
      <span className={`inline-flex items-center gap-1 text-[8px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full border ${tones[verdictTone]}`}>
        {analyzing
          ? <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-pulse" />
          : <span className={`w-1.5 h-1.5 rounded-full ${dots[verdictTone]}`} />
        }
        {verdictLabel}
      </span>
    </div>
  );
}

function ScreeningVisual() {
  return (
    <div className="w-full h-full bg-canvas p-4 flex flex-col gap-1.5">
      {/* "Generate with AI" panel */}
      <div className="bg-white rounded-xl shadow-card px-3 py-2 flex items-center gap-2 border border-accent/30">
        <Sparkles className="w-3 h-3 text-accent" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-ink-3 flex-1">
          Generate with AI
        </span>
        <span className="inline-flex items-center gap-1 text-[9px] font-bold text-white bg-accent px-1.5 py-0.5 rounded">
          <Zap className="w-2.5 h-2.5" />
          Generate
        </span>
      </div>
      {/* mini Q rows like in ScreeningEditor with category tag */}
      {[
        { n: 'Q1', cat: 'hard skills', text: 'Walk me through the most recent K8s production incident you led.' },
        { n: 'Q2', cat: 'behavioral', text: 'Describe a time you disagreed with a peer engineer on architecture.' },
      ].map((q) => (
        <div key={q.n} className="bg-white rounded-xl shadow-card px-3 py-2 flex items-start gap-2">
          <span className="text-[9px] uppercase tracking-wider font-bold text-ink-3 mt-0.5">{q.n}</span>
          <div className="flex-1 min-w-0">
            <span className="inline-flex items-center gap-1 text-[8px] uppercase tracking-wider font-bold text-accent bg-accent/5 px-1.5 py-0.5 rounded-full">
              <Sparkles className="w-2.5 h-2.5" />
              {q.cat}
            </span>
            <p className="text-[11px] text-ink-1 leading-snug truncate mt-0.5">{q.text}</p>
          </div>
        </div>
      ))}
      {/* link panel */}
      <div className="bg-white rounded-xl shadow-card px-3 py-2 flex items-center gap-2 mt-auto">
        <code className="text-[10px] text-ink-2 truncate flex-1">jobskills.work/screen/79ey74otz5...</code>
        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-ink-1 bg-canvas px-2 py-1 rounded-lg">
          <Copy className="w-3 h-3" /> Copy
        </span>
      </div>
    </div>
  );
}

function ReportVisual() {
  return (
    <div className="w-full h-full bg-canvas p-4 flex flex-col gap-2">
      {/* AI verdict + overall match */}
      <div className="bg-white rounded-xl shadow-card px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-3 h-3 text-accent" />
          <span className="text-[10px] uppercase tracking-wider font-bold text-ink-3">BARS scorecard</span>
        </div>
        <div className="text-right">
          <p className="text-[16px] font-bold text-emerald-600 leading-none">81<span className="text-[10px] text-ink-3">/100</span></p>
        </div>
      </div>
      {/* mini bars */}
      <div className="bg-white rounded-xl shadow-card px-3 py-2.5 flex flex-col gap-1.5">
        <BarRow icon="🧩" label="Analytical" pct={80} tone="bg-emerald-500" />
        <BarRow icon="🎯" label="Ownership" pct={100} tone="bg-emerald-500" />
        <BarRow icon="💬" label="Communication" pct={60} tone="bg-amber-500" />
        <BarRow icon="⚡" label="Domain Expertise" pct={80} tone="bg-emerald-500" />
      </div>
      {/* summary teaser */}
      <div className="bg-white rounded-xl shadow-card px-3 py-2 flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold text-ink-1">Verdict — Advance to hiring manager</p>
          <p className="text-[9.5px] text-ink-3 line-clamp-1 mt-0.5">Strong technical owner; communication uneven on cross-team coordination questions.</p>
        </div>
        <ChevronDown className="w-3 h-3 text-ink-3 mt-0.5" />
      </div>
    </div>
  );
}

function BarRow({ icon, label, pct, tone }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-0.5">
        <span className="text-[10px]">{icon}</span>
        <span className="text-[10px] text-ink-1 flex-1 truncate">{label}</span>
        <span className="text-[9px] font-bold text-ink-3">{Math.round(pct / 20)}/5</span>
      </div>
      <div className="h-1 bg-canvas rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function BillingVisual() {
  return (
    <div className="w-full h-full bg-canvas p-4 flex flex-col gap-2">
      {/* current plan */}
      <div className="bg-white rounded-xl shadow-card px-3 py-2.5 flex items-center justify-between">
        <div>
          <span className="text-[9px] uppercase tracking-wider font-bold text-ink-3">Current plan</span>
          <p className="text-[12px] font-bold text-ink-1">Agency · $149/mo</p>
        </div>
        <span className="text-[8px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-accent text-white">
          Recommended
        </span>
      </div>
      {/* usage progress */}
      <div className="bg-white rounded-xl shadow-card px-3 py-2.5">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[9px] uppercase tracking-wider font-bold text-ink-3">Usage — May</span>
          <span className="text-[10px] font-bold text-ink-1">47 / 100</span>
        </div>
        <div className="h-1.5 bg-canvas rounded-full overflow-hidden">
          <div className="h-full rounded-full bg-accent" style={{ width: '47%' }} />
        </div>
      </div>
      {/* AI spend widget */}
      <div className="bg-white rounded-xl shadow-card px-3 py-2.5">
        <div className="flex items-center justify-between mb-1">
          <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold text-ink-3">
            <Sparkles className="w-2.5 h-2.5 text-accent" />
            AI spend
          </span>
          <span className="text-[12px] font-bold text-ink-1">$0.71</span>
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          <Mini label="Calls" value="184" />
          <Mini label="Cands" value="47" />
          <Mini label="Reports" value="32" />
        </div>
      </div>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="rounded-lg bg-canvas px-2 py-1">
      <p className="text-[8px] uppercase tracking-wider font-bold text-ink-3">{label}</p>
      <p className="text-[10px] font-bold text-ink-1">{value}</p>
    </div>
  );
}
